import { useEffect, useMemo, useState } from 'react'
import { Typography, Box, Stack, Collapse, Chip, Divider, Button } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { useForm } from 'react-hook-form'
import dayjs from 'dayjs'
import { Dropdown } from '@common/components/Form/Dropdown/Dropdown'
import { SPACING } from '@common/theme/spacing'
import { SIZING } from '@common/theme/sizing'
import { StudyQueryDetailDataSchema, StudyQueryMetadataSchema, StudyQueryUpdateSchema } from '@common/config/api/client'
import { capitalize } from '@common/utils/stringUtils'
import TimePointSubmissionStatusChip from '@features/subject/components/TimePointSubmissionStatusChip'
import { getStatusChipColor } from './QueriesTable'
import { formatQueryStatus } from './types'

export interface QueryDetailsPanelProps {
  query: StudyQueryDetailDataSchema
  metadata: StudyQueryMetadataSchema
  onCancel: () => void
  onSave: (updatedData: StudyQueryUpdateSchema) => void
  isUpdating?: boolean
  isReadOnly?: boolean
}

export default function QueryDetailsPanel({
  query,
  metadata,
  onSave,
  isUpdating = false,
  isReadOnly = false,
}: Readonly<QueryDetailsPanelProps>) {
  const [detailsExpanded, setDetailsExpanded] = useState(true)
  const methods = useForm<StudyQueryUpdateSchema>({
    defaultValues: {
      query_assignee_ids: query?.assignees?.map((assignee) => assignee.id) || [],
      cc_user_ids: query?.cc_users?.map((user) => user.id) || [],
      study_timepoint_container_submission_ids:
        query?.linked_container_submissions?.map((container) => container.id) || [],
    },
  })

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = methods

  const toggleDetailsSection = () => {
    setDetailsExpanded(!detailsExpanded)
  }

  const handleSaveChanges = (data: StudyQueryUpdateSchema) => {
    onSave(data)
  }

  const containerOptions = metadata.timepoint_container_submissions?.map((container) => ({
    label: container.name,
    value: container.id,
  }))

  const assigneeOptions = useMemo(() => {
    return metadata?.assignee_users
      ? metadata.assignee_users.map((user) => ({
          label: user.email ? `${user.name} (${user.email})` : user.name,
          value: user.id,
          description: user.role,
        }))
      : []
  }, [metadata?.assignee_users])

  const ccUserOptions = useMemo(() => {
    return metadata?.cc_users
      ? metadata.cc_users.map((user) => ({
          label: user.email ? `${user.name} (${user.email})` : user.name,
          value: user.id,
          description: user.role,
        }))
      : []
  }, [metadata?.cc_users])

  useEffect(() => {
    methods.reset({
      query_assignee_ids: query?.assignees?.map((assignee) => assignee.id) || [],
      cc_user_ids: query?.cc_users?.map((user) => user.id) || [],
      study_timepoint_container_submission_ids:
        query?.linked_container_submissions?.map((container) => container.id) || [],
    })
  }, [query, metadata, methods])

  const createdAtDisplay = useMemo(() => {
    if (!query?.created_at) {
      return '-'
    }
    return dayjs(query.created_at).format('DD/MM/YYYY, hh:mm:ss A')
  }, [query?.created_at])

  const categoryDisplay = useMemo(() => {
    if (!query?.category) {
      return '-'
    }
    return query.category.includes(':') ? query.category : capitalize(query.category)
  }, [query?.category])

  const queryTitleDisplay = query?.messages?.[0]?.title || '-'

  const labelSx = {
    width: SIZING.infoLabelWidth,
    minWidth: SIZING.infoLabelWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
  } as const

  const valueSx = {
    flex: 1,
    minWidth: 0,
  } as const

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
        }}
      >
        <form id="updateQueryForm" onSubmit={handleSubmit(handleSaveChanges)}>
          <Box sx={{ paddingX: SPACING.drawerHeaderSpacingX }}>
            <Stack
              justifyContent="space-between"
              alignItems="center"
              direction="row"
              sx={{
                paddingY: SPACING.spacingLg,
                cursor: 'pointer',
              }}
              onClick={toggleDetailsSection}
            >
              <Typography variant="subtitle1">Details</Typography>
              {detailsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Stack>
            <Collapse in={detailsExpanded}>
              <Stack spacing={SPACING.spacingXxl} sx={{ paddingY: SPACING.spacingXl }}>
                <Stack direction="row" spacing={SPACING.spacingXl} alignItems="flex-start">
                  <Typography variant="subtitle2" color="text.secondary" sx={labelSx}>
                    Query status
                  </Typography>
                  <Chip
                    label={formatQueryStatus(query?.status)}
                    color={getStatusChipColor(query?.status)}
                    size="small"
                    sx={{ borderRadius: SPACING.borderRadiusXxl }}
                  />
                </Stack>

                <Stack direction="row" spacing={SPACING.spacingXl} alignItems="flex-start">
                  <Typography variant="subtitle2" color="text.secondary" sx={labelSx}>
                    Query category
                  </Typography>
                  <Typography variant="body1" sx={valueSx}>
                    {categoryDisplay}
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={SPACING.spacingXl} alignItems="flex-start">
                  <Typography variant="subtitle2" color="text.secondary" sx={labelSx}>
                    Query title
                  </Typography>
                  <Typography variant="body1" sx={valueSx}>
                    {queryTitleDisplay}
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={SPACING.spacingXl} alignItems="flex-start">
                  <Typography variant="subtitle2" color="text.secondary" sx={labelSx}>
                    Created by
                  </Typography>
                  <Typography variant="body1" sx={valueSx}>
                    {query?.created_by?.name ?? '-'}
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={SPACING.spacingXl} alignItems="flex-start">
                  <Typography variant="subtitle2" color="text.secondary" sx={labelSx}>
                    Created at
                  </Typography>
                  <Typography variant="body1" sx={valueSx}>
                    {createdAtDisplay}
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={SPACING.spacingXl} alignItems="flex-start">
                  <Typography variant="subtitle2" color="text.secondary" sx={labelSx}>
                    Subject ID
                  </Typography>
                  <Typography variant="body1" sx={valueSx}>
                    {query?.subject?.pseudo_id}
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={SPACING.spacingXl} alignItems="flex-start">
                  <Typography variant="subtitle2" color="text.secondary" sx={labelSx}>
                    Timepoint
                  </Typography>
                  <Typography variant="body1" sx={valueSx}>
                    {query?.timepoint?.name}
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={SPACING.spacingXl} alignItems="flex-start">
                  <Typography variant="subtitle2" color="text.secondary" sx={labelSx}>
                    Timepoint status
                  </Typography>
                  <TimePointSubmissionStatusChip status={query?.timepoint?.status} size="small" />
                </Stack>

                <Stack direction="row" spacing={SPACING.spacingXl} alignItems="flex-start">
                  <Typography variant="subtitle2" color="text.secondary" sx={labelSx}>
                    Site {query?.site?.pseudo_id ? ' ID' : ''}
                  </Typography>
                  <Typography variant="body1" sx={valueSx}>
                    {query?.site?.name}
                    {query?.site?.pseudo_id ? ` (${query.site.pseudo_id})` : ''}
                  </Typography>
                </Stack>

                {isReadOnly ? (
                  <Stack direction="row" spacing={SPACING.spacingXl} alignItems="flex-start">
                    <Typography variant="subtitle2" color="text.secondary" sx={labelSx}>
                      Containers
                    </Typography>
                    <Typography variant="body1" sx={valueSx}>
                      {query?.linked_container_submissions?.map((container) => container.name).join(', ') || 'None'}
                    </Typography>
                  </Stack>
                ) : (
                  <Dropdown
                    name="study_timepoint_container_submission_ids"
                    control={control}
                    label="Container"
                    options={containerOptions ?? []}
                    size="small"
                    multiple
                  />
                )}

                {isReadOnly ? (
                  <>
                    <Stack direction="row" spacing={SPACING.spacingXl} alignItems="flex-start">
                      <Typography variant="subtitle2" color="text.secondary" sx={labelSx}>
                        Assignees
                      </Typography>
                      <Typography variant="body1" sx={valueSx}>
                        {query?.assignees?.map((user) => user.name).join(', ') || 'None'}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={SPACING.spacingXl} alignItems="flex-start">
                      <Typography variant="subtitle2" color="text.secondary" sx={labelSx}>
                        CC Users
                      </Typography>
                      <Typography variant="body1" sx={valueSx}>
                        {query?.cc_users?.map((user) => user.name).join(', ') || 'None'}
                      </Typography>
                    </Stack>
                  </>
                ) : (
                  <>
                    <Dropdown
                      name="query_assignee_ids"
                      label="Assignee"
                      options={assigneeOptions}
                      control={control}
                      size="small"
                      multiple
                      helperText="Users can re-submit uploads and send messages"
                      showOptionDescription
                    />

                    <Dropdown
                      name="cc_user_ids"
                      label="CC users"
                      options={ccUserOptions}
                      control={control}
                      size="small"
                      multiple
                      helperText="Users can view details and chat but cannot send messages or re-submit uploads"
                      showOptionDescription
                    />
                  </>
                )}
              </Stack>
            </Collapse>
          </Box>

          <Divider />
        </form>
      </Box>
      {!isReadOnly && (
        <Box
          sx={{
            padding: SPACING.spacingXl,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            variant="contained"
            color="primary"
            type="submit"
            form="updateQueryForm"
            disabled={!isDirty || isUpdating}
          >
            {isUpdating ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      )}
    </Box>
  )
}
